import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_VENDORS = 'FETCH_VENDORS';
export const FETCH_VENDORS_SUCCESS = 'FETCH_VENDORS_SUCCESS';
export const FETCH_VENDORS_FAIL = 'FETCH_VENDORS_FAIL';
export const CLEAR_VENDORS = 'CLEAR_VENDORS';
export const CREATE_VENDOR = 'CREATE_VENDOR';
export const CREATE_VENDOR_SUCCESS = 'CREATE_VENDOR_SUCCESS';
export const CREATE_VENDOR_FAIL = 'CREATE_VENDOR_FAIL';
export const UPDATE_VENDOR = 'UPDATE_VENDOR';
export const UPDATE_VENDOR_SUCCESS = 'UPDATE_VENDOR_SUCCESS';
export const UPDATE_VENDOR_FAIL = 'UPDATE_VENDOR_FAIL';
export const DELETE_VENDOR = 'DELETE_VENDOR';
export const DELETE_VENDOR_SUCCESS = 'DELETE_VENDOR_SUCCESS';
export const DELETE_VENDOR_FAIL = 'DELETE_VENDOR_FAIL';

// Reducer
export default function vendorsReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_VENDORS:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_VENDORS_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_VENDORS_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_VENDORS:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_VENDOR_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_VENDOR_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_VENDOR_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchVendors(payload) {
  return { type: FETCH_VENDORS, payload };
}

export function fetchVendorsSuccess(payload) {
  return { type: FETCH_VENDORS_SUCCESS, payload };
}

export function fetchVendorsFail(payload) {
  return { type: FETCH_VENDORS_FAIL, payload };
}

export function clearVendors(payload) {
  return { type: CLEAR_VENDORS, payload };
}

export function createVendor(payload) {
  return { type: CREATE_VENDOR, payload };
}

export function createVendorSuccess(payload) {
  return { type: CREATE_VENDOR_SUCCESS, payload };
}

export function createVendorFail(payload) {
  return { type: CREATE_VENDOR_FAIL, payload };
}

export function updateVendor(payload) {
  return { type: UPDATE_VENDOR, payload };
}

export function updateVendorSuccess(payload) {
  return { type: UPDATE_VENDOR_SUCCESS, payload };
}

export function updateVendorFail(payload) {
  return { type: UPDATE_VENDOR_FAIL, payload };
}

export function deleteVendor(payload) {
  return { type: DELETE_VENDOR, payload };
}

export function deleteVendorSuccess(payload) {
  return { type: DELETE_VENDOR_SUCCESS, payload };
}

export function deleteVendorFail(payload) {
  return { type: DELETE_VENDOR_FAIL, payload };
}

// Requests

let abort;

export function requestFetchVendors(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = payload
    ? abortableCallApi(`inventory/vendor/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('inventory/vendor/');
  abort = request.abort;
  return request.res;
}

export function requestSearchVendors({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('inventory/vendor/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'Vendor Name',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateVendor(payload) {
  return callApi('inventory/vendor/', 'POST', payload);
}

export function requestUpdateVendor(payload) {
  return callApi('inventory/vendor/', 'PATCH', payload);
}

export function requestDeleteVendor(payload) {
  return callApi(`inventory/vendor/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchVendorsSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchVendors, action.payload)
      : yield call(requestFetchVendors, action.payload);
    yield put(fetchVendorsSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchVendorsFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createVendorSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateVendor, values);
    yield put(createVendorSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New vendor successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createVendorFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateVendorSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateVendor, values);
    yield put(updateVendorSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Vendor successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateVendorFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteVendorSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteVendor, ID);
    yield put(deleteVendorSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Vendor successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteVendorFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete vendor',
      variant: 'warning',
    }));
  }
}

export function* vendorsSaga() {
  yield takeLatest(FETCH_VENDORS, fetchVendorsSaga);
  yield takeEvery(CREATE_VENDOR, createVendorSaga);
  yield takeEvery(UPDATE_VENDOR, updateVendorSaga);
  yield takeEvery(DELETE_VENDOR, deleteVendorSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/vendors.js